// Re:Focus — Settings Module
// Settings modal, API keys, audit log, AI name, pro mode, cap ambient

import { e } from './utils.js';
import { SESSION_KEY, getAuthHeader } from './auth-client.js';

let _trapFocus = () => () => {};
let _activeFocusTrapRef = { value: null };
let _focusTrapTriggerRef = { value: null };
let _renderFromState = () => {};
let _getAiName = () => 'Cap';
let _setAiName = () => {};

const PRO_MODE_KEY = 'ff_pro_mode';

export function isProMode() { return localStorage.getItem(PRO_MODE_KEY) === '1'; }

export function initSettings({ trapFocus, activeFocusTrapRef, focusTrapTriggerRef, renderFromState, getAiName, setAiName }) {
  _trapFocus = trapFocus;
  _activeFocusTrapRef = activeFocusTrapRef;
  _focusTrapTriggerRef = focusTrapTriggerRef;
  _renderFromState = renderFromState;
  _getAiName = getAiName;
  _setAiName = setAiName;
}

export function openSettings() {
  _focusTrapTriggerRef.value = document.activeElement;
  const modal = document.getElementById('settings-modal');
  modal.classList.remove('hidden');
  const toggle = document.getElementById('pro-mode-toggle');
  if (toggle) toggle.checked = isProMode();
  loadApiKeys();
  loadAiWebhookStatus();
  if (_activeFocusTrapRef.value) _activeFocusTrapRef.value();
  _activeFocusTrapRef.value = _trapFocus(modal);
}

export function closeSettings() {
  document.getElementById('settings-modal').classList.add('hidden');
  document.getElementById('new-key-reveal').classList.add('hidden');
  if (_activeFocusTrapRef.value) { _activeFocusTrapRef.value(); _activeFocusTrapRef.value = null; }
  if (_focusTrapTriggerRef.value) { _focusTrapTriggerRef.value.focus(); _focusTrapTriggerRef.value = null; }
}

async function loadApiKeys() {
  const token = localStorage.getItem(SESSION_KEY);
  if (!token) return;
  const listEl = document.getElementById('api-keys-list');
  const auditEl = document.getElementById('audit-log-list');
  listEl.innerHTML = '<div class="api-keys-loading">Loading…</div>';
  try {
    const res = await fetch('/api/v1/keys', { headers: { 'Authorization': `Bearer ${token}` } });
    const data = await res.json();
    if (!res.ok) { listEl.innerHTML = `<div class="api-keys-error">${e(data.error || 'Error loading keys')}</div>`; return; }

    const keys = (data.keys || []).filter(k => !k.revokedAt);
    if (keys.length === 0) {
      listEl.innerHTML = '<div class="api-keys-empty">No keys yet. Generate one below.</div>';
    } else {
      listEl.innerHTML = keys.map(k => `
        <div class="api-key-row" data-key-id="${e(k.id)}">
          <div class="api-key-info">
            <span class="api-key-label">${e(k.label)}</span>
            <span class="api-key-scope scope-${e(k.scope)}">${e(k.scope)}</span>
            <span class="api-key-hint">…${e(k.hint)}</span>
            <span class="api-key-meta">${k.lastUsed ? 'Last used ' + new Date(k.lastUsed).toLocaleDateString() : 'Never used'}</span>
          </div>
          <button class="api-key-revoke-btn" data-key-id="${e(k.id)}" title="Revoke key">Revoke</button>
        </div>
      `).join('');
      listEl.querySelectorAll('.api-key-revoke-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          if (!confirm(`Revoke key "${btn.closest('.api-key-row').querySelector('.api-key-label').textContent}"? This cannot be undone.`)) return;
          btn.disabled = true; btn.textContent = 'Revoking…';
          await fetch('/api/v1/keys', {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: btn.dataset.keyId }),
          });
          loadApiKeys();
        });
      });
    }

    // Audit log
    const auditLog = data.auditLog || [];
    if (auditLog.length === 0) {
      auditEl.innerHTML = '<div class="api-keys-empty">No API activity yet.</div>';
    } else {
      auditEl.innerHTML = `<table class="audit-table">
        <thead><tr><th>Time</th><th>Key</th><th>Action</th><th>Resource</th></tr></thead>
        <tbody>${auditLog.slice(0, 20).map(entry => `
          <tr>
            <td>${new Date(entry.ts).toLocaleString()}</td>
            <td>${e(entry.keyLabel || entry.keyId?.slice(-8) || '—')}</td>
            <td>${e(entry.action)}</td>
            <td>${e(entry.resource)}</td>
          </tr>`).join('')}
        </tbody>
      </table>`;
    }
  } catch(err) {
    listEl.innerHTML = '<div class="api-keys-error">Failed to load keys.</div>';
  }
}

export function setupSettingsListeners() {
  document.getElementById('settings-btn').addEventListener('click', openSettings);
  document.querySelector('.settings-close-btn').addEventListener('click', closeSettings);
  document.getElementById('settings-modal').addEventListener('click', (ev) => {
    if (ev.target === document.getElementById('settings-modal')) closeSettings();
  });

  // ── Professional mode toggle ──
  const proModeToggle = document.getElementById('pro-mode-toggle');
  if (proModeToggle) {
    proModeToggle.addEventListener('change', () => {
      localStorage.setItem(PRO_MODE_KEY, proModeToggle.checked ? '1' : '0');
    });
  }

  // ── Cap Ambient mode (P1-6) ──
  const capAmbientRadios = document.querySelectorAll('input[name="capAmbientMode"]');
  const savedAmbient = localStorage.getItem('capAmbientMode') || 'all';
  capAmbientRadios.forEach(r => {
    if (r.value === savedAmbient) r.checked = true;
    r.addEventListener('change', () => {
      localStorage.setItem('capAmbientMode', r.value);
      _renderFromState();
    });
  });

  // ── Create API key ──
  document.getElementById('create-key-btn').addEventListener('click', async () => {
    const token = localStorage.getItem(SESSION_KEY);
    const label = document.getElementById('new-key-label').value.trim();
    const scope = document.getElementById('new-key-scope').value;
    if (!label) {
      const hint = document.getElementById('key-label-hint');
      if (hint) hint.classList.remove('hidden');
      document.getElementById('new-key-label').focus();
      return;
    }
    const hint = document.getElementById('key-label-hint');
    if (hint) hint.classList.add('hidden');

    const btn = document.getElementById('create-key-btn');
    btn.disabled = true; btn.textContent = 'Generating…';

    try {
      const res = await fetch('/api/v1/keys', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ label, scope }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Failed to create key'); return; }

      // Show the key ONCE
      document.getElementById('new-key-value').textContent = data.key;
      document.getElementById('new-key-reveal').classList.remove('hidden');
      document.getElementById('new-key-label').value = '';
      document.getElementById('copy-key-confirm').classList.add('hidden');

      // Update MCP snippet with the new key
      document.getElementById('mcp-snippet').textContent = `{
  "mcpServers": {
    "refocus": {
      "command": "npx",
      "args": ["-y", "refocus-mcp"],
      "env": {
        "REFOCUS_API_KEY": "${data.key}"
      }
    }
  }
}`;
      loadApiKeys();
    } finally {
      btn.disabled = false; btn.textContent = '+ Generate Key';
    }
  });

  // ── Copy API key ──
  document.getElementById('copy-key-btn').addEventListener('click', () => {
    const val = document.getElementById('new-key-value').textContent;
    try {
      navigator.clipboard.writeText(val).then(() => {
        document.getElementById('copy-key-confirm').classList.remove('hidden');
        setTimeout(() => document.getElementById('copy-key-confirm').classList.add('hidden'), 2000);
      }).catch(() => { prompt('Copy this key:', val); });
    } catch { prompt('Copy this key:', val); }
  });

  // ── AI Name settings ──
  const aiNameInput = document.getElementById('ai-name-input');
  const aiNameSaveBtn = document.getElementById('ai-name-save-btn');
  if (aiNameInput && aiNameSaveBtn) {
    aiNameInput.value = _getAiName();
    aiNameSaveBtn.addEventListener('click', async () => {
      const newName = aiNameInput.value.trim() || 'Cap';
      aiNameSaveBtn.disabled = true;
      aiNameSaveBtn.textContent = 'Saving\u2026';
      try {
        await fetch('/api/board', {
          method: 'PATCH',
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ aiName: newName }),
        });
        _setAiName(newName);
        _renderFromState();
        aiNameSaveBtn.textContent = 'Saved!';
        setTimeout(() => { aiNameSaveBtn.textContent = 'Save'; aiNameSaveBtn.disabled = false; }, 1500);
      } catch (err) {
        console.warn('[settings] AI name save failed:', err);
        aiNameSaveBtn.textContent = 'Save';
        aiNameSaveBtn.disabled = false;
      }
    });
  }

  // ── AI Push webhook ──
  const webhookSaveBtn = document.getElementById('save-ai-webhook-btn');
  if (webhookSaveBtn) {
    webhookSaveBtn.addEventListener('click', async () => {
      const url = document.getElementById('ai-webhook-url')?.value?.trim() || '';
      const secret = document.getElementById('ai-webhook-secret')?.value?.trim() || '';
      const status = document.getElementById('ai-webhook-status');

      webhookSaveBtn.disabled = true;
      webhookSaveBtn.textContent = 'Saving…';
      try {
        const body = { webhookUrl: url };
        if (secret) body.webhookSecret = secret;
        const resp = await fetch('/api/v1/ai-connection', {
          method: 'PUT',
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await resp.json();
        if (resp.ok) {
          if (status) { status.textContent = 'Saved'; status.className = 'chpw-status chpw-ok'; status.classList.remove('hidden'); }
          document.getElementById('ai-webhook-secret').value = '';
          loadAiWebhookStatus();
        } else {
          if (status) { status.textContent = data.error || 'Save failed'; status.className = 'chpw-status chpw-err'; status.classList.remove('hidden'); }
        }
      } catch (err) {
        if (status) { status.textContent = 'Network error'; status.className = 'chpw-status chpw-err'; status.classList.remove('hidden'); }
      }
      webhookSaveBtn.disabled = false;
      webhookSaveBtn.textContent = 'Save';
      setTimeout(() => { if (status) status.classList.add('hidden'); }, 3000);
    });
  }
}

// ── AI Push webhook status ──
async function loadAiWebhookStatus() {
  try {
    const resp = await fetch('/api/v1/ai-connection', { headers: getAuthHeader() });
    if (!resp.ok) return;
    const data = await resp.json();
    const urlInput = document.getElementById('ai-webhook-url');
    if (urlInput && data.webhookUrl) urlInput.value = data.webhookUrl;

    const info = document.getElementById('ai-webhook-info');
    if (!info) return;
    const parts = [];
    if (data.hasSecret) parts.push('Secret configured');
    if (data.lastDelivery) parts.push(`Last delivery: ${new Date(data.lastDelivery).toLocaleString()}`);
    if (data.degraded) parts.push('<span style="color:var(--danger)">Degraded — re-save to reset</span>');
    else if (data.failureCount > 0) parts.push(`${data.failureCount} recent failure(s)`);
    info.innerHTML = parts.join(' · ') || '';
  } catch { /* offline */ }
}
